import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  MessageSquare, Save, Send, CheckCircle, XCircle, RefreshCw,
  Shield, Clock, Key, Phone
} from 'lucide-react';

const SMSSettingsPage = () => {
  const [settings, setSettings] = useState({
    api_key: '',
    sender_id: '',
    enabled: false,
    notify_new_ticket: true,
    notify_sla_breach: true,
    notify_low_stock: false
  });
  const [apiKeyMasked, setApiKeyMasked] = useState('');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Test SMS din MAINTIX - Configurare reusita!');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [settingsRes, logsRes] = await Promise.all([
        api.get('/sms/settings'),
        api.get('/sms/logs?limit=10')
      ]);
      const s = settingsRes.data;
      setSettings({
        api_key: '',
        sender_id: s.sender_id || '',
        enabled: s.enabled || false,
        notify_new_ticket: s.notify_new_ticket !== false,
        notify_sla_breach: s.notify_sla_breach !== false,
        notify_low_stock: s.notify_low_stock || false
      });
      setApiKeyMasked(s.api_key_masked || '');
      setLogs(logsRes.data);
    } catch (error) {
      toast.error('Eroare la incarcarea setarilor SMS');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/sms/settings', settings);
      toast.success('Setari SMS salvate cu succes');
      fetchData();
    } catch (error) {
      toast.error('Eroare la salvarea setarilor');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testPhone) { toast.error('Introduceti numarul de telefon'); return; }
    setTesting(true);
    try {
      await api.post('/sms/test', { phone: testPhone, message: testMessage });
      toast.success(`SMS test trimis catre ${testPhone}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la trimiterea SMS de test');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sms-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Configurari SMS</h1>
        <p className="text-slate-500 text-sm mt-1">Integrare SMSO.ro pentru notificari SMS automate</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Connection Settings */}
        <Card data-testid="sms-config-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Key className="w-4 h-4 text-blue-600" />
              Configurare SMSO.ro
            </CardTitle>
            <CardDescription>
              Obtineti API key-ul din contul SMSO.ro → Setari → API
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>API Key SMSO.ro</Label>
              <Input
                type="password"
                value={settings.api_key}
                onChange={e => setSettings({ ...settings, api_key: e.target.value })}
                placeholder={apiKeyMasked || 'Introduceti API key-ul SMSO.ro'}
                data-testid="sms-api-key-input"
              />
              {apiKeyMasked && !settings.api_key && (
                <p className="text-xs text-green-600">Cheie existenta: {apiKeyMasked}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Sender ID (numeric)</Label>
              <Input
                value={settings.sender_id}
                onChange={e => setSettings({ ...settings, sender_id: e.target.value })}
                placeholder="Ex: 4"
                data-testid="sms-sender-input"
              />
              <p className="text-xs text-slate-500">
                ID-ul numeric al expeditorului din SMSO.ro (gasiti-l in contul SMSO.ro → Expeditori). Nu introduceti numele, ci numarul ID.
              </p>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Activare SMS</p>
                <p className="text-xs text-slate-500">Permite trimiterea automata de SMS-uri</p>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={v => setSettings({ ...settings, enabled: v })}
                data-testid="sms-enabled-switch"
              />
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full" data-testid="save-sms-settings-btn">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salveaza Setari
            </Button>
          </CardContent>
        </Card>

        {/* Notification Preferences */}
        <Card data-testid="sms-notifications-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-green-600" />
              Notificari SMS Automate
            </CardTitle>
            <CardDescription>Selectati evenimentele care vor genera SMS automat</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Tichet Nou</p>
                <p className="text-xs text-slate-500">SMS la crearea unui tichet nou</p>
              </div>
              <Switch
                checked={settings.notify_new_ticket}
                onCheckedChange={v => setSettings({ ...settings, notify_new_ticket: v })}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">SLA Depasit</p>
                <p className="text-xs text-slate-500">SMS cand un tichet depaseste SLA</p>
              </div>
              <Switch
                checked={settings.notify_sla_breach}
                onCheckedChange={v => setSettings({ ...settings, notify_sla_breach: v })}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">Stoc Scazut</p>
                <p className="text-xs text-slate-500">SMS cand un articol este sub stocul minim</p>
              </div>
              <Switch
                checked={settings.notify_low_stock}
                onCheckedChange={v => setSettings({ ...settings, notify_low_stock: v })}
              />
            </div>

            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-700">
                SMS-urile vor fi trimise catre numarul de telefon configurat in profilul fiecarui utilizator cu rol de admin sau manager.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Test SMS */}
        <Card data-testid="sms-test-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Send className="w-4 h-4 text-amber-600" />
              Test SMS
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Numar Telefon</Label>
              <Input
                value={testPhone}
                onChange={e => setTestPhone(e.target.value)}
                placeholder="+40722334455"
                data-testid="test-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Mesaj Test</Label>
              <Input
                value={testMessage}
                onChange={e => setTestMessage(e.target.value)}
                data-testid="test-message-input"
              />
            </div>
            <Button onClick={handleTest} disabled={testing || !testPhone} variant="outline" className="w-full" data-testid="send-test-sms-btn">
              {testing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Trimite SMS Test
            </Button>
          </CardContent>
        </Card>

        {/* SMS Logs */}
        <Card data-testid="sms-logs-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-600" />
              Istoric SMS
            </CardTitle>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">Niciun SMS trimis inca</p>
            ) : (
              <div className="space-y-2">
                {logs.map((log, i) => (
                  <div
                    key={i}
                    className="border rounded-lg text-sm cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedLog(expandedLog === i ? null : i)}
                    data-testid={`sms-log-${i}`}
                  >
                    <div className="flex items-center justify-between p-2.5">
                      <div className="flex items-center gap-2 min-w-0">
                        {log.status === 'sent' ? (
                          <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500 shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="font-medium">{log.to}</p>
                          {expandedLog !== i && (
                            <p className="text-xs text-slate-500 truncate max-w-[250px]">{log.body}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-2">
                        <Badge variant={log.status === 'sent' ? 'default' : 'destructive'} className={`text-xs ${log.status === 'sent' ? 'bg-green-600' : ''}`}>
                          {log.status === 'sent' ? 'Trimis' : 'Esuat'}
                        </Badge>
                        <p className="text-xs text-slate-400 mt-1">
                          {log.sent_at ? new Date(log.sent_at).toLocaleString('ro-RO') : '-'}
                        </p>
                      </div>
                    </div>
                    {expandedLog === i && (
                      <div className="px-3 pb-3 pt-0 border-t mx-2.5 mt-0 space-y-1.5">
                        <div className="pt-2">
                          <p className="text-xs font-medium text-slate-400 uppercase">Mesaj</p>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{log.body}</p>
                        </div>
                        {log.sent_to_name && (
                          <div>
                            <p className="text-xs font-medium text-slate-400 uppercase">Destinatar</p>
                            <p className="text-sm text-slate-700">{log.sent_to_name}</p>
                          </div>
                        )}
                        {log.type && (
                          <div>
                            <p className="text-xs font-medium text-slate-400 uppercase">Tip</p>
                            <p className="text-sm text-slate-700">{log.type === 'new_ticket' ? 'Tichet Nou (automat)' : log.type === 'test' ? 'Test' : log.type}</p>
                          </div>
                        )}
                        {log.error && (
                          <div>
                            <p className="text-xs font-medium text-slate-400 uppercase">Eroare</p>
                            <p className="text-sm text-red-600 break-words">{log.error}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SMSSettingsPage;
